/**
 * Small pure helpers for the room: naming devices and the shared security code.
 * Kept free of DOM and WebRTC so they can be unit-tested.
 */

export type DeviceKind = "phone" | "tablet" | "computer";

// River-lodge flavour: every device gets an otter-ish name like "Whiskered Rivermaster".
const ADJECTIVES = [
  "Whiskered", "Rippling", "Mossy", "Pebbly", "Splashing", "Driftwood", "Riverbank",
  "Slippery", "Bubbly", "Paddling", "Dappled", "Reedy", "Muddy", "Frosty", "Glossy",
  "Nimble", "Rowdy", "Sleek", "Snug", "Brackish", "Silty", "Streamlined", "Dewy",
  "Boulder", "Willow", "Marshy", "Foamy", "Skipping", "Twiggy", "Damp", "Gurgling",
  "Sunlit", "Wading", "Frisky", "Plucky", "Mellow", "Cozy", "Salmon", "Ripply", "Snappy",
];

/**
 * A stable, friendly two-word name (adjective + animal) derived from a seed —
 * the device's own id. the second word is always "Otter"; the room de-duplicates a repeated
 * adjective by appending a number. Deterministic so it never changes
 * mid-session and matches what the device announces to its peers.
 */
export function funnyName(seed: string): string {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  const adjective = ADJECTIVES[h % ADJECTIVES.length];
  return `${adjective} Otter`;
}

/** A coarse device category, for picking an icon. */
export function deviceKind(ua: string): DeviceKind {
  if (/iPad/i.test(ua)) return "tablet";
  if (/iPhone|iPod/i.test(ua)) return "phone";
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? "phone" : "tablet";
  if (/Tablet/i.test(ua)) return "tablet";
  return "computer";
}

/** A short human label for this device, derived from the user agent. */
export function deviceLabel(ua: string): string {
  if (/iPad/.test(ua)) return "iPad";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/Android/.test(ua)) {
    // "Linux; Android 14; Pixel 8 Build/..." or "...; Pixel 8) AppleWebKit"
    const model = /Android[^;)]*;\s*([^;)]+?)(?:\s+Build|\))/.exec(ua)?.[1]?.trim();
    if (model && !/^[A-Z]{1,2}$/.test(model) && model.toLowerCase() !== "k") {
      return model;
    }
    return "Android phone";
  }
  if (/CrOS/.test(ua)) return "Chromebook";

  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : null;

  const platform = /Macintosh/.test(ua)
    ? "Mac"
    : /Windows/.test(ua)
      ? "Windows PC"
      : /Linux/.test(ua)
        ? "Linux PC"
        : "Computer";

  return browser ? `${platform} · ${browser}` : platform;
}

/** "iPhone", "iPhone" → "iPhone", "iPhone (2)" so two identical devices stay tellable apart. */
export function disambiguate(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((name) => {
    const n = (seen.get(name) ?? 0) + 1;
    seen.set(name, n);
    return n === 1 ? name : `${name} (${n})`;
  });
}

const WORDS = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"];

/** "Four devices" reads better in a serif headline than "4 devices". */
export function countWord(n: number): string {
  return WORDS[n] ?? String(n);
}
